import {
  getAddress,
  keccak256,
  parseEventLogs,
  type Address,
  type PublicClient,
  type WalletClient,
} from "viem";
import type { LauncherClient } from "./launcherClient";
import {
  LauncherError,
  type AdminOutcome,
  type DeployPhase,
  type FactoryStatus,
  type FeeQuote,
  type GasEstimate,
  type Hex,
  type LaunchOutcome,
  type HistoryPage,
  type TokenConfig,
  type RecipientEntry,
  type VerificationCheck,
  type VerificationResult,
} from "./types";
import { tokenFactoryAbi, fixedSupplyTokenAbi } from "../../config/factoryAbi";
import { ACTIVE_CHAIN } from "../../config/chains";
import { TOKEN_DECIMALS } from "../../config/limits";
import { robinhoodTestnet } from "../../wallet/chain";
import { scanTokenCreated } from "../history/eventHistory";

/**
 * Distinguish a true transport failure (RPC unreachable / timed out) from an
 * on-chain simulation revert. A revert means the node answered and rejected
 * the call — telling the user to "check your connection" for a revert hides
 * real configuration/contract bugs (and did exactly that in production).
 */
export function classifySimulationError(err: unknown): LauncherError {
  // Walk the FULL cause chain first: viem wraps transport failures inside
  // ContractFunctionExecutionError → EstimateGasExecutionError →
  // HttpRequestError/TimeoutError, so wrapper class names alone are not
  // evidence of a revert. Transport markers anywhere in the chain win.
  let sawRevert = false;
  let e: unknown = err;
  for (let depth = 0; depth < 8 && e instanceof Error; depth++) {
    const name = e.name ?? "";
    if (
      name === "HttpRequestError" ||
      name === "TimeoutError" ||
      name === "WebSocketRequestError" ||
      (name === "RpcRequestError" && /timeout|limit|unavailable/i.test(e.message))
    ) {
      return new LauncherError("rpcError", "RPC transport failure during simulation");
    }
    if (
      name === "ContractFunctionRevertedError" ||
      /reverted|execution reverted/i.test(e.message)
    ) {
      sawRevert = true; // revert-specific evidence; keep walking for transport markers
    }
    e = (e as Error & { cause?: unknown }).cause;
  }
  if (sawRevert) {
    return new LauncherError("simulationRevert", "On-chain simulation reverted");
  }
  // Unknown shape: fail closed as a transport error (safe retry allowed).
  return new LauncherError("rpcError", "Gas estimation failed");
}

const REQUIRED_CHAIN_ID = 46630;
const ZERO = "0x0000000000000000000000000000000000000000";

export interface ViemLauncherDeps {
  publicClient: PublicClient;
  walletClient?: WalletClient;
  factoryAddress: Hex;
  expectedBytecodeHash: Hex;
  chainId: number;
  /** Block the factory was deployed at. REQUIRED — history scans start here. */
  deploymentBlock: bigint;
}

export class ViemLauncherClient implements LauncherClient {
  readonly isMock = false;
  constructor(private readonly d: ViemLauncherDeps) {
    // Enforce the invariant at the client boundary: a live client can never be
    // constructed to silently scan from block 0.
    if (typeof d.deploymentBlock !== "bigint" || d.deploymentBlock < 0n) {
      throw new LauncherError("factoryNotConfigured", "A valid factory deployment block is required.");
    }
  }

  private get factory(): Address {
    return this.d.factoryAddress as Address;
  }

  private read<T>(functionName: string, args: unknown[] = []): Promise<T> {
    return this.d.publicClient.readContract({
      address: this.factory,
      abi: tokenFactoryAbi,
      functionName: functionName as never,
      args: args as never,
    }) as Promise<T>;
  }

  /** Reject any chain that is not Robinhood Chain Testnet (46630). */
  private async assertActiveChain(): Promise<void> {
    if (this.d.chainId !== REQUIRED_CHAIN_ID) throw new LauncherError("wrongNetwork");
    const pub = await this.d.publicClient.getChainId();
    if (pub !== REQUIRED_CHAIN_ID) throw new LauncherError("wrongNetwork");
    if (this.d.walletClient) {
      const w = await this.d.walletClient.getChainId();
      if (w !== REQUIRED_CHAIN_ID) throw new LauncherError("wrongNetwork");
    }
  }

  async observedBytecodeHash(): Promise<Hex | null> {
    const code = await this.d.publicClient.getBytecode({ address: this.factory });
    if (!code || code === "0x") return null;
    return keccak256(code);
  }

  async verifyBytecode(): Promise<boolean> {
    const h = await this.observedBytecodeHash();
    return !!h && h.toLowerCase() === this.d.expectedBytecodeHash.toLowerCase();
  }

  async getFactoryStatus(): Promise<FactoryStatus> {
    const observed = await this.observedBytecodeHash();
    const [launchFee, owner] = await Promise.all([
      this.creationFee(),
      this.read<Hex>("owner"),
    ]);
    // Canonical ETH-v1 factory is immutable and has no pause switch.
    const paused = false;
    return {
      configured: true,
      address: this.d.factoryAddress,
      chainId: this.d.chainId,
      bytecodeVerified: !!observed && observed.toLowerCase() === this.d.expectedBytecodeHash.toLowerCase(),
      observedBytecodeHash: observed,
      paused,
      launchFee,
      owner,
      demo: false,
    };
  }

  /**
   * Creation fee from the canonical ETH-v1 factory's immutable economics.
   * getEconomicConfig() returns 11 uints; index 10 is creationFee (wei).
   */
  private async creationFee(): Promise<bigint> {
    const cfg = await this.read<readonly bigint[]>("getEconomicConfig");
    const fee = cfg[10];
    if (fee === undefined) throw new LauncherError("unknown", "Malformed getEconomicConfig response");
    return fee;
  }

  getLaunchFee(): Promise<bigint> {
    return this.creationFee();
  }
  isFeeExempt(_account: Hex): Promise<boolean> {
    // Canonical ETH-v1 factory has no fee exemptions — everyone pays the flat fee.
    return Promise.resolve(false);
  }
  effectiveLaunchFee(_account: Hex): Promise<bigint> {
    return this.creationFee();
  }
  getOwner(): Promise<Hex> {
    return this.read<Hex>("owner");
  }
  isPaused(): Promise<boolean> {
    // Canonical ETH-v1 factory is immutable — no pause switch exists.
    return Promise.resolve(false);
  }
  getBalance(account: Hex): Promise<bigint> {
    return this.d.publicClient.getBalance({ address: account as Address });
  }

  /**
   * Build a padded RecipientConfig tuple for ABI encoding (ETH-v1 createMarket).
   * Pads to exactly 5 slots using the zero address / 0 bps.
   * If `entries` is empty, defaults to a single recipient: `account` @ 10 000 bps.
   */
  private buildRecipientConfig(entries: RecipientEntry[], account: Hex): {
    recipients: [Address, Address, Address, Address, Address];
    splitsBps:  [bigint, bigint, bigint, bigint, bigint];
    count: bigint;
  } {
    const effective = entries.length > 0 ? entries : [{ address: account, bps: 10_000 }];
    const ZERO_ADDR = ZERO as Address;
    const addrs:   Address[] = effective.map((r) => getAddress(r.address) as Address);
    const bpss:    bigint[]  = effective.map((r) => BigInt(r.bps));
    while (addrs.length < 5) { addrs.push(ZERO_ADDR); bpss.push(0n); }
    return {
      recipients: addrs as [Address, Address, Address, Address, Address],
      splitsBps:  bpss  as [bigint, bigint, bigint, bigint, bigint],
      count:      BigInt(effective.length),
    };
  }

  async getFeeQuote(account: Hex): Promise<FeeQuote> {
    const [publicFee, effectiveFee] = await Promise.all([
      this.getLaunchFee(),
      this.effectiveLaunchFee(account),
    ]);
    return { publicFee, effectiveFee, exempt: effectiveFee === 0n };
  }

  async estimateCreateGas(config: TokenConfig, account: Hex): Promise<GasEstimate> {
    const value = await this.effectiveLaunchFee(account);
    let gasUnits: bigint;
    try {
      if (config.recipients !== undefined) {
        // ETH-v1 fair-launch path — createMarket with RecipientConfig
        const rc = this.buildRecipientConfig(config.recipients, account);
        gasUnits = await this.d.publicClient.estimateContractGas({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createMarket",
          args: [config.name, config.symbol, rc],
          account: account as Address,
          value,
        });
      } else {
        // Legacy simple-token path — createToken
        gasUnits = await this.d.publicClient.estimateContractGas({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createToken",
          args: [config.name, config.symbol, config.supplyWhole * 10n ** TOKEN_DECIMALS],
          account: account as Address,
          value,
        });
      }
    } catch (err) {
      throw classifySimulationError(err);
    }
    let gasPriceWei: bigint;
    try {
      gasPriceWei = await this.d.publicClient.getGasPrice();
    } catch {
      throw new LauncherError("rpcError", "Gas price unavailable");
    }
    return { gasUnits, gasPriceWei, estimatedGasCostWei: gasUnits * gasPriceWei };
  }

  async createToken(
    config: TokenConfig,
    account: Hex,
    opts?: { knownFeeAtReview?: bigint; onPhase?: (p: DeployPhase) => void }
  ): Promise<LaunchOutcome> {
    const onPhase = opts?.onPhase;
    if (!this.d.walletClient) throw new LauncherError("unknown", "No wallet connected");
    onPhase?.("preparing");
    await this.assertActiveChain();

    onPhase?.("revalidatingFactory");
    const observed = await this.observedBytecodeHash();
    if (!observed) throw new LauncherError("bytecodeMismatch", "No factory bytecode");
    if (observed.toLowerCase() !== this.d.expectedBytecodeHash.toLowerCase()) {
      throw new LauncherError("bytecodeMismatch");
    }
    if (await this.isPaused()) throw new LauncherError("factoryPaused");

    // Fee reread + change detection.
    const value = await this.effectiveLaunchFee(account);
    if (opts?.knownFeeAtReview !== undefined && opts.knownFeeAtReview !== value) {
      throw new LauncherError("feeChanged", "The effective fee changed since review.", {
        was: opts.knownFeeAtReview.toString(),
        now: value.toString(),
      });
    }

    // ETH-v1 fair-launch markets use createMarket (recipients required).
    // Legacy simple-token path uses createToken (supply required).
    const useMarket = config.recipients !== undefined;
    const rc = useMarket ? this.buildRecipientConfig(config.recipients!, account) : null;
    const supplySmallestUnits = useMarket
      ? 1_000_000_000n * 10n ** TOKEN_DECIMALS  // ETH-v1 fixed supply
      : config.supplyWhole * 10n ** TOKEN_DECIMALS;

    onPhase?.("estimatingGas");
    let gas: bigint;
    try {
      if (useMarket) {
        gas = await this.d.publicClient.estimateContractGas({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createMarket",
          args: [config.name, config.symbol, rc!],
          account: account as Address,
          value,
        });
      } else {
        gas = await this.d.publicClient.estimateContractGas({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createToken",
          args: [config.name, config.symbol, supplySmallestUnits],
          account: account as Address,
          value,
        });
      }
    } catch (err) {
      // Fail closed: never proceed on an unknown gas cost. A revert is
      // surfaced as simulationRevert (config/contract issue), a transport
      // failure as rpcError (retryable).
      throw classifySimulationError(err);
    }

    onPhase?.("checkingBalance");
    const balance = await this.getBalance(account);
    let gasPrice: bigint;
    try {
      gasPrice = await this.d.publicClient.getGasPrice();
    } catch {
      // Fail closed: a missing gas price must NOT collapse the balance check to
      // "fee only" (which would let an underfunded wallet through).
      throw new LauncherError("rpcError", "Gas price unavailable");
    }
    if (balance < value + gas * gasPrice) throw new LauncherError("insufficientBalance");

    onPhase?.("awaitingApproval");
    // Confirm the wallet's active signing address matches the UI account BEFORE
    // any approval prompt; reject a mismatch rather than sign as the wrong wallet.
    const [signer] = await this.d.walletClient.getAddresses();
    if (!signer || getAddress(signer) !== getAddress(account)) {
      throw new LauncherError("accountMismatch");
    }
    let txHash: Hex;
    try {
      if (useMarket) {
        // ETH-v1 path: simulateContract first to catch reverts before signing.
        await this.d.publicClient.simulateContract({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createMarket",
          args: [config.name, config.symbol, rc!],
          account: account as Address,
          value,
        });
        txHash = await this.d.walletClient.writeContract({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createMarket",
          args: [config.name, config.symbol, rc!],
          account: account as Address,
          chain: robinhoodTestnet, // explicit — never chain: null
          value,
        });
      } else {
        txHash = await this.d.walletClient.writeContract({
          address: this.factory,
          abi: tokenFactoryAbi,
          functionName: "createToken",
          args: [config.name, config.symbol, supplySmallestUnits],
          account: account as Address,
          chain: robinhoodTestnet, // explicit — never chain: null
          value,
        });
      }
    } catch (e) {
      throw mapWalletError(e);
    }

    onPhase?.("submitted");
    onPhase?.("waitingReceipt");
    let receipt;
    try {
      receipt = await this.d.publicClient.waitForTransactionReceipt({ hash: txHash });
    } catch (e) {
      const msg = e instanceof Error ? e.message.toLowerCase() : "";
      if (msg.includes("replac")) throw new LauncherError("replaced");
      throw new LauncherError("rpcError", "Failed to fetch receipt");
    }
    if (receipt.status !== "success") throw new LauncherError("reverted");

    onPhase?.("verifying");
    // Wrap decoding so a malformed/unexpected log can never hide the confirmed tx.
    let created: { address: string; args: unknown } | undefined;
    let decodeFailed = false;
    let marketAddress: Hex | undefined;
    try {
      if (useMarket) {
        // ETH-v1 fair-launch: parse MarketCreated event.
        const events = parseEventLogs({
          abi: tokenFactoryAbi,
          logs: receipt.logs,
          eventName: "MarketCreated",
        });
        const found = events.find((e) => getAddress(e.address) === getAddress(this.d.factoryAddress));
        if (found) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const args = found.args as any;
          marketAddress = args.market as Hex;
          // Re-shape to the shape runVerification expects (token/creator/name/symbol).
          created = {
            address: found.address,
            args: {
              token:    args.token,
              creator:  args.creator,
              name:     args.name,
              symbol:   args.symbol,
              // feePaid not emitted by MarketCreated — fill from value so it degrades gracefully.
              feePaid:  value,
              // totalSupply not emitted — fill with ETH-v1 fixed supply.
              totalSupply: supplySmallestUnits,
            },
          };
        }
      } else {
        const events = parseEventLogs({
          abi: tokenFactoryAbi,
          logs: receipt.logs,
          eventName: "TokenCreated",
        });
        created = events.find((e) => getAddress(e.address) === getAddress(this.d.factoryAddress));
      }
    } catch {
      decodeFailed = true;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ev = created?.args as any;

    // On-chain block timestamp where available (not Date.now()); resilient.
    let blockTimestamp: number | null = null;
    try {
      const block = await this.d.publicClient.getBlock({ blockNumber: receipt.blockNumber });
      blockTimestamp = Number(block.timestamp);
    } catch {
      blockTimestamp = null;
    }

    // Re-check the factory runtime hash AFTER the tx; resilient. Passed into
    // verification as a named factoryHashAfter check.
    let observedNow: Hex | null = null;
    try {
      observedNow = await this.observedBytecodeHash();
    } catch {
      observedNow = null;
    }

    const verification = await this.runVerification(
      created?.address as Hex | undefined,
      ev,
      config,
      supplySmallestUnits,
      value,
      account,
      blockTimestamp,
      observedNow,
      decodeFailed
    );

    onPhase?.("confirmed");
    return {
      transactionConfirmed: true,
      verificationPassed: verification.passed,
      verificationFailures: verification.failures,
      verification,
      receipt: {
        tokenAddress: (ev?.token as Hex) ?? (ZERO as Hex),
        marketAddress,
        creator: (ev?.creator as Hex) ?? account,
        factoryAddress: this.d.factoryAddress,
        name: config.name,
        symbol: config.symbol,
        decimals: 18,
        supplyWhole: useMarket ? 1_000_000_000n : config.supplyWhole,
        supplySmallestUnits,
        feePaid: (ev?.feePaid as bigint) ?? value,
        txHash,
        blockNumber: receipt.blockNumber,
        chainId: this.d.chainId,
        timestamp: blockTimestamp ?? 0,
        expectedFactoryHash: this.d.expectedBytecodeHash,
        observedFactoryHash: observedNow,
        transactionStatus: "success",
        demonstrationMode: false,
      },
    };
  }

  /** Full post-deploy verification. Uses the ACTUAL emitted creator, no hardcoding. */
  private async runVerification(
    emittingFactory: Hex | undefined,
    ev: Record<string, unknown> | undefined,
    config: TokenConfig,
    supplySmallestUnits: bigint,
    effectiveFee: bigint,
    expectedCreator: Hex,
    blockTimestamp: number | null,
    observedFactoryHashAfter: Hex | null,
    decodeFailed: boolean
  ): Promise<VerificationResult> {
    const checks: VerificationCheck[] = [];
    const add = (key: string, label: string, passed: boolean, expected?: string, observed?: string) =>
      checks.push({ key, label, passed, ...(expected !== undefined ? { expected } : {}), ...(observed !== undefined ? { observed } : {}) });

    if (decodeFailed) {
      add("eventDecode", "TokenCreated event could not be decoded", false);
    }

    // Factory runtime hash re-checked AFTER the transaction.
    add(
      "factoryHashAfter",
      "Factory runtime hash unchanged after deployment",
      observedFactoryHashAfter !== null &&
        observedFactoryHashAfter.toLowerCase() === this.d.expectedBytecodeHash.toLowerCase(),
      this.d.expectedBytecodeHash,
      observedFactoryHashAfter ?? "unavailable"
    );

    add(
      "blockTimestamp",
      "Block timestamp available",
      blockTimestamp !== null,
      undefined,
      blockTimestamp === null ? "unavailable" : String(blockTimestamp)
    );

    const eventPresent = !!ev;
    add("eventPresent", "TokenCreated event present", eventPresent);
    if (!ev) return finalise(checks);

    add(
      "eventFactory",
      "Event emitted by configured factory",
      !!emittingFactory && getAddress(emittingFactory) === getAddress(this.d.factoryAddress),
      this.d.factoryAddress,
      emittingFactory
    );

    const eventCreator = ev.creator as Hex | undefined;
    add(
      "eventCreator",
      "Event creator is the submitting wallet",
      !!eventCreator && getAddress(eventCreator) === getAddress(expectedCreator),
      expectedCreator,
      eventCreator
    );

    const tokenAddress = ev.token as Hex | undefined;
    add("tokenNonZero", "Token address is non-zero", !!tokenAddress && tokenAddress !== ZERO, undefined, tokenAddress);
    add("eventName", "Event name matches", ev.name === config.name, config.name, String(ev.name));
    add("eventSymbol", "Event symbol matches", ev.symbol === config.symbol, config.symbol, String(ev.symbol));
    add(
      "eventSupply",
      "Event supply matches",
      (ev.totalSupply as bigint) === supplySmallestUnits,
      supplySmallestUnits.toString(),
      String(ev.totalSupply)
    );
    add(
      "eventFee",
      "Event feePaid matches effective fee",
      (ev.feePaid as bigint) === effectiveFee,
      effectiveFee.toString(),
      String(ev.feePaid)
    );

    const creator = ev.creator as Hex | undefined;

    if (tokenAddress && tokenAddress !== ZERO) {
      try {
        const code = await this.d.publicClient.getBytecode({ address: tokenAddress as Address });
        const hasCode = !!code && code !== "0x";
        add("tokenBytecode", "Token bytecode exists", hasCode);
        if (hasCode) {
          const rd = <T>(fn: string, args: unknown[] = []) =>
            this.d.publicClient.readContract({
              address: tokenAddress as Address,
              abi: fixedSupplyTokenAbi,
              functionName: fn as never,
              args: args as never,
            }) as Promise<T>;
          const [name, symbol, decimals, total, bal] = await Promise.all([
            rd<string>("name"),
            rd<string>("symbol"),
            rd<number>("decimals"),
            rd<bigint>("totalSupply"),
            creator ? rd<bigint>("balanceOf", [creator]) : Promise.resolve(0n),
          ]);
          add("erc20Name", "ERC-20 name() matches", name === config.name, config.name, name);
          add("erc20Symbol", "ERC-20 symbol() matches", symbol === config.symbol, config.symbol, symbol);
          add("erc20Decimals", "ERC-20 decimals() == 18", Number(decimals) === 18, "18", String(decimals));
          add("erc20Supply", "ERC-20 totalSupply() matches", total === supplySmallestUnits, supplySmallestUnits.toString(), total.toString());
          add(
            "creatorBalance",
            "Creator holds the full supply",
            !!creator && bal === supplySmallestUnits,
            supplySmallestUnits.toString(),
            bal.toString()
          );
        }
      } catch {
        // A post-confirmation RPC failure must NOT lose the confirmed transaction.
        add(
          "verificationRpc",
          "Transaction confirmed, but one or more post-deployment RPC verification calls failed.",
          false
        );
      }
    }
    return finalise(checks);
  }

  async setFeeExempt(account: Hex, exempt: boolean): Promise<AdminOutcome> {
    // Canonical ETH-v1 factory has no fee-exemption admin surface — the
    // creation fee is immutable and applies to everyone.
    void account;
    void exempt;
    throw new LauncherError(
      "unknown",
      "Fee exemptions are not supported by the canonical ETH-v1 factory — the creation fee is immutable.",
    );
  }

  getMyLaunches(account: Hex, cursor?: bigint | null): Promise<HistoryPage> {
    return scanTokenCreated(this.d.publicClient, {
      factory: this.d.factoryAddress,
      creator: account,
      fromBlock: this.d.deploymentBlock,
      ...(cursor != null ? { beforeBlock: cursor } : {}),
    });
  }
  getRecentLaunches(cursor?: bigint | null): Promise<HistoryPage> {
    return scanTokenCreated(this.d.publicClient, {
      factory: this.d.factoryAddress,
      fromBlock: this.d.deploymentBlock,
      ...(cursor != null ? { beforeBlock: cursor } : {}),
    });
  }
}

function finalise(checks: VerificationCheck[]): VerificationResult {
  const failures = checks.filter((c) => !c.passed);
  return { passed: failures.length === 0, checks, failures };
}

function mapWalletError(e: unknown): LauncherError {
  const msg = (e instanceof Error ? e.message : String(e)).toLowerCase();
  if (msg.includes("user rejected") || msg.includes("user denied")) return new LauncherError("userRejected");
  if (msg.includes("insufficient funds")) return new LauncherError("insufficientBalance");
  if (msg.includes("chain") && msg.includes("mismatch")) return new LauncherError("wrongNetwork");
  if (msg.includes("replac")) return new LauncherError("replaced");
  return new LauncherError("reverted", e instanceof Error ? e.message : undefined);
}

export const EXPECTED_FACTORY_HASH = ACTIVE_CHAIN.expectedFactoryBytecodeHash;
